import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = axios.create({ baseURL: `${BACKEND_URL}/api` });

// Attach token to every request
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('rebuilt_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // On login endpoints a 401 is an expected bad-creds response — let the caller
      // handle the toast. Do NOT blow away the page or local storage.
      const url = error.config?.url || '';
      const isLoginAttempt = /\/(auth\/(admin|agent)-(login|mobile-login|login-identifier)|employee\/login)(\?|$)/.test(url);
      if (!isLoginAttempt) {
        localStorage.removeItem('rebuilt_token');
        localStorage.removeItem('rebuilt_user');
        localStorage.removeItem('rebuilt_role');
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const sendOTP = (mobile, referral_code) => API.post('/auth/send-otp', { mobile, referral_code });
export const verifyOTP = (mobile, otp, referral_code) => API.post('/auth/verify-otp', { mobile, otp, referral_code });
export const adminLogin = (email, password) => API.post('/auth/admin-login', { email, password });
export const agentLogin = (email, password) => API.post('/auth/agent-login', { email, password });
export const agentMobileLogin = (mobile, password) => API.post('/auth/agent-mobile-login', { mobile, password });
// Unified login — accepts mobile OR email as identifier
export const agentIdentifierLogin = (identifier, password) => API.post('/auth/agent-login-identifier', { identifier, password });
export const agentRegister = (data) => API.post('/auth/agent-register', data);
export const agentUploadPhoto = (formData) => API.post('/auth/agent-register/upload-photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const agentForgotPassword = (mobile) => API.post('/auth/agent-forgot-password', { mobile });
export const agentResetPassword = (mobile, otp, new_password) => API.post('/auth/agent-reset-password', { mobile, otp, new_password });
export const getMe = () => API.get('/auth/me');

// Customer
export const getProfile = () => API.get('/customer/profile');
export const updateProfile = (data) => API.put('/customer/profile', data);
export const verifyPAN = (pan_number, name) => API.post('/customer/verify-pan', { pan_number, name });
export const updateEmployment = (data) => API.put('/customer/employment', data);
export const uploadDocument = (formData) => API.post('/customer/upload-document', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const uploadAadhaar = (formData) => API.post('/customer/upload-aadhaar', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// P1 Fix 8: Selfie upload for application
export const uploadApplicationSelfie = (formData) => API.post('/customer/upload-selfie', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// P1 Fix 1: Employee endpoints
export const employeeLogin = (employee_id, password) => API.post('/employee/login', { employee_id, password });
export const employeeMe = () => API.get('/employee/me');
export const employeeReferralInfo = () => API.get('/employee/referral-info');
export const employeeMyAgents = () => API.get('/employee/my-agents');

// Admin employee CRUD
export const adminListEmployees = () => API.get('/admin/employees');
export const adminCreateEmployee = (data) => API.post('/admin/employees', data);
export const adminUpdateEmployee = (id, data) => API.put(`/admin/employees/${id}`, data);
export const adminDeleteEmployee = (id) => API.delete(`/admin/employees/${id}`);
export const adminUpdateEmployeePermissions = (id, permissions) => API.put(`/admin/employees/${id}/permissions`, { permissions });
export const adminResetEmployeePassword = (id, password) => API.put(`/admin/employees/${id}/reset-password`, { password });
export const adminGetEmployeeAgents = (id) => API.get(`/admin/employees/${id}/agents`);
export const getDocuments = () => API.get('/customer/documents');
export const getAadhaarStatus = () => API.get('/customer/aadhaar-status');
// Digio DigiLocker Aadhaar verification
export const createDigilockerRequest = () => API.post('/kyc/create-digilocker-request');
export const getKycAadhaarStatus = () => API.get('/kyc/aadhaar-status');
export const initiatePayment = (data) => API.post('/customer/initiate-payment', data);
export const verifyPayment = (data) => API.post('/customer/verify-payment', data);
export const reconcilePayment = () => API.post('/customer/reconcile-payment');
export const getSubscription = () => API.get('/customer/subscription');
export const getSubscriptionPrice = () => API.get('/customer/subscription-price');
export const getDashboard = () => API.get('/customer/dashboard');

// P3: Customer Applications (new)
export const createApplication = (data) => API.post('/customer/applications', data);
export const getApplications = () => API.get('/customer/applications');
export const getEmiCalculator = (amount, months) => API.get('/customer/emi-calculator', { params: { amount, months } });

// Legacy application endpoint
export const createLegacyApplication = (data) => API.post('/customer/application', data);

// P6: Customer Repayments
export const getRepayments = () => API.get('/customer/repayments');
export const initiateRepayment = (repaymentId) => API.post(`/customer/repayments/${repaymentId}/pay`);

// Agent
export const getAgentDashboard = () => API.get('/agent/dashboard');
export const getAgentCustomers = () => API.get('/agent/customers');
export const getAgentCustomerDetail = (id) => API.get(`/agent/customer/${id}`);
export const agentOnboard = (mobile) => API.post('/agent/onboard-customer', { mobile });
export const getAgentReferralCode = () => API.get('/agent/referral-code');
export const getAgentCommissions = () => API.get('/agent/commissions');
export const getAgentNotifications = () => API.get('/agent/notifications');
export const markAgentNotificationsRead = () => API.put('/agent/notifications/read-all');

// Admin
export const adminGetUsers = (params) => API.get('/admin/users', { params });
export const adminGetUser = (id) => API.get(`/admin/user/${id}`);
export const adminUpdateKYC = (id, kyc_status) => API.put(`/admin/user/${id}/kyc-status`, { kyc_status });
export const adminReviewAadhaar = (id, action, reason) => API.put(`/admin/user/${id}/aadhaar-review`, { action, reason });
export const adminBlockUser = (id) => API.put(`/admin/user/${id}/block`);
export const adminGetApplications = (params) => API.get('/admin/applications', { params });
export const adminApproveApp = (id, remarks) => API.put(`/admin/application/${id}/approve`, { remarks });
export const adminRejectApp = (id, remarks) => API.put(`/admin/application/${id}/reject`, { remarks });
export const adminReviewApp = (id, action, remarks) => API.put(`/admin/application/${id}/review`, { action, remarks });
export const adminGetDisbursements = (params) => API.get('/admin/disbursements', { params });
export const adminCreateDisbursement = (data) => API.post('/admin/disbursements', data);
export const adminApproveDisbursement = (id) => API.put(`/admin/disbursement/${id}/approve`);
export const adminTransferDisbursement = (id, utr_number) => API.put(`/admin/disbursement/${id}/transfer`, { utr_number });
// Admin Agents & Applications
export const adminGetAgents = (params) => API.get('/admin/agents', { params });
export const adminCreateAgent = (data) => API.post('/admin/agents', data);
export const adminToggleAgent = (id) => API.put(`/admin/agent/${id}/status`);
export const adminGetAgentApplications = (params) => API.get('/admin/agent-applications', { params });
export const adminGetAgentApplicationDetail = (id) => API.get(`/admin/agent-applications/${id}`);
export const adminApproveAgent = (id) => API.post(`/admin/agent-applications/${id}/approve`);
export const adminRejectAgent = (id, reason) => API.post(`/admin/agent-applications/${id}/reject`, { reason });
export const adminGetContracts = () => API.get('/admin/contracts');
export const adminGetMandates = () => API.get('/admin/mandates');
export const adminGetDocumentUrl = (id) => API.get(`/admin/document-url/${id}`);

// Admin Commissions
export const adminGetCommissions = (params) => API.get('/admin/commissions', { params });
export const adminPayCommission = (id, data) => API.put(`/admin/commission/${id}/pay`, data);
export const adminPayBulkCommissions = (data) => API.put('/admin/commissions/pay-bulk', data);
export const adminBackfillCommissions = () => API.post('/admin/commissions/backfill');

// Admin Aadhaar Review Queue (P1)
export const adminGetAadhaarQueue = () => API.get('/admin/aadhaar/pending-queue');
export const adminBulkReviewAadhaar = (user_ids, action, reason) => API.post('/admin/aadhaar/bulk-review', { user_ids, action, reason });

// Admin EMI Cron
export const adminRunEmiCron = (force = false) => API.post(`/admin/emi/run-cron${force ? '?force=true' : ''}`);

// Admin Reports (P8)
export const adminGetDashboard = () => API.get('/admin/dashboard');
export const adminGetSubscriptionReport = () => API.get('/admin/reports/subscriptions');
export const adminGetApplicationReport = () => API.get('/admin/reports/applications');
export const adminGetDisbursementReport = () => API.get('/admin/reports/disbursements');
export const adminGetAgentReport = () => API.get('/admin/reports/agents');
export const adminGetCollectionReport = () => API.get('/admin/reports/collections');
export const adminGetRevenueReport = () => API.get('/admin/reports/revenue');
export const adminGetAuditLogs = (params) => API.get('/admin/audit-logs', { params });
export const adminGetRepayments = (params) => API.get('/admin/repayments', { params });

// Admin Settings (P9)
export const adminGetSettings = (category) => API.get('/admin/settings', { params: category ? { category } : {} });
export const adminUpdateSetting = (key, value, description) => API.put(`/admin/settings/${key}`, { value, description });
// v7.12 System health
export const adminSystemHealth = () => API.get('/system/health-status');
export const adminEnvAudit = (live = false) => API.get('/system/env-audit', { params: live ? { live: true } : {} });
export const adminRunPaymentWatchdog = (dryRun = false) => API.post('/system/payment-watchdog/run', null, { params: dryRun ? { dry_run: true } : {} });

// Notifications
export const getNotifications = () => API.get('/notifications');
export const markAllNotificationsRead = () => API.put('/notifications/read-all');
export const markNotificationRead = (id) => API.put(`/notifications/${id}/read`);

// Admin Notifications
export const adminGetNotifications = () => API.get('/admin/notifications');
export const adminMarkNotificationsRead = () => API.put('/admin/notifications/read-all');

// Contract & Mandate
export const getContract = (appId) => API.get(`/customer/contract/${appId}`);
export const downloadContract = (appId) => API.get(`/customer/contract/${appId}/download`, { responseType: 'blob' });
export const acceptContract = (appId) => API.post(`/customer/contract-accept/${appId}`);
export const setupMandate = (appId, data) => API.post(`/customer/mandate-setup/${appId}`, data);
export const getMandate = (appId) => API.get(`/customer/mandate/${appId}`);
export const getEMISchedule = (appId) => API.get(`/customer/emi-schedule/${appId}`);
export const getDisbursements = () => API.get('/customer/disbursements');
export const initiateEMIPayment = (emiId) => API.post(`/customer/initiate-emi-payment/${emiId}`);

// UPI Autopay Mandate
export const createUPISubscription = (data) => API.post('/mandate/create-upi-subscription', data);
export const getMandateStatus = (mandateId) => API.get(`/mandate/status/${mandateId}`);
export const triggerEMIReminders = () => API.get('/mandate/trigger-emi-reminders');

// Report Exports
export const exportReport = (type, fmt) => API.get(`/admin/export/${type}/${fmt}`, { responseType: 'blob' });

// Push
export const pushSubscribe = (subscription) => API.post('/push/subscribe', subscription);

// App Release (admin APK upload + version history + rollback)
export const adminGetAppVersion = () => API.get('/app/version');
export const adminUploadApk = (file, versionName, versionCode, onUploadProgress) => {
  const fd = new FormData();
  fd.append('apk', file);
  const params = {};
  if (versionName) params.version_name = versionName;
  if (versionCode) params.version_code = versionCode;
  return API.post('/admin/app/upload-apk', fd, {
    params,
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
    timeout: 600000, // 10 minutes — APKs can be large
  });
};
export const adminListApkVersions = () => API.get('/admin/app/versions');
export const adminActivateApkVersion = (id) => API.post(`/admin/app/versions/${id}/activate`);
export const adminDeleteApkVersion = (id) => API.delete(`/admin/app/versions/${id}`);
// Note: admin download uses a query-param JWT (Authorization header can't be set on <a download>)
export const adminApkVersionDownloadUrl = (id) => {
  const t = localStorage.getItem('rebuilt_token') || '';
  return `${process.env.REACT_APP_BACKEND_URL}/api/admin/app/versions/${id}/download?token=${encodeURIComponent(t)}`;
};

export default API;
